/**
 * 文档存储服务
 * 实现ITextDocumentStorageService接口，通过TextDocumentStorageProvider提供文档存储功能
 *
 * 核心功能：
 * 1. 连接到底层存储提供者
 * 2. 实现业务逻辑
 * 3. 提供事务支持
 * 4. 处理重复文档检测和验证
 */
import { splitIntoParagraphs, TextDocumentStorageProvider, } from '@readsavor/utils';
import { v4 as uuidv4 } from 'uuid';
import { TranslationStorageService } from './TranslationStorageService.js';
import { LoggerService } from '../../logging/services/logger.service.js';
/**
 * 文档存储服务
 * 实现ITextDocumentStorageService接口，处理业务逻辑
 */
export class DocumentStorageService {
    /**
     * 私有构造函数
     */
    constructor() {
        this.initialized = false;
        this.initPromise = null;
        // 获取日志服务
        this.logger = LoggerService.getInstance();
        // 获取存储提供者单例
        this.provider = TextDocumentStorageProvider.getInstance();
    }
    /**
     * 获取单例实例
     */
    static getInstance() {
        if (!DocumentStorageService.instance) {
            DocumentStorageService.instance = new DocumentStorageService();
        }
        return DocumentStorageService.instance;
    }
    /**
     * 初始化服务
     * 确保提供者就绪
     */
    async initialize() {
        if (this.initialized)
            return;
        if (this.initPromise)
            return this.initPromise;
        this.initPromise = (async () => {
            try {
                await this.provider.initialize();
                this.initialized = true;
            }
            catch (error) {
                this.logger.error('[DocumentStorageService] Initialization failed', { error });
                this.initialized = false;
                throw error;
            }
            finally {
                this.initPromise = null;
            }
        })();
        return this.initPromise;
    }
    /**
     * 确保初始化
     */
    async ensureInitialized() {
        if (!this.initialized) {
            await this.initialize();
        }
    }
    /**
     * 添加文本文档
     * 处理内容，创建段落，存储文档
     */
    async addTextDocument(text, metadata) {
        await this.ensureInitialized();
        const duplicateNumericId = await this.findDuplicateDocument(text, metadata.language, metadata.targetLanguage);
        if (duplicateNumericId !== null) {
            await this.provider.updateDocument({ id: duplicateNumericId, type: 'text' }, {
                lastAccessTime: Date.now(),
            });
            return { id: duplicateNumericId, type: 'text' };
        }
        // 处理文本成段落
        const paragraphsData = splitIntoParagraphs(text);
        const totalParagraphs = paragraphsData.length;
        // 准备文档元数据
        const now = Date.now();
        const docTitle = metadata.title || this.generateTitle(text);
        const docPreview = text.substring(0, 100) + (text.length > 100 ? '...' : '');
        // 创建文档对象
        const newDocument = {
            contentType: 'text',
            title: docTitle,
            timestamp: now,
            language: metadata.language,
            targetLanguage: metadata.targetLanguage,
            preview: docPreview,
            size: text.length,
            totalParagraphs: totalParagraphs,
            lastAccessTime: now,
        };
        // 在事务中存储文档和段落
        try {
            const docIdentifier = await this.provider.transaction(async () => {
                const newDocIdentifier = await this.provider.addDocument(newDocument);
                const paragraphsToStore = paragraphsData.map((pData, index) => ({
                    textId: newDocIdentifier.id,
                    paragraphId: pData.id || uuidv4(),
                    index: index,
                    content: pData.content,
                }));
                await this.provider.addParagraphs(paragraphsToStore);
                await this.provider.getDocument(newDocIdentifier);
                return newDocIdentifier;
            });
            return docIdentifier;
        }
        catch (error) {
            this.logger.error('[DocumentStorageService] Failed to add document', { error });
            throw new Error('Failed to add document: ' + (error instanceof Error ? error.message : String(error)));
        }
    }
    /**
     * 获取文档
     */
    async getDocument(identity) {
        return this.getTextDocument(identity);
    }
    /**
     * 获取文档
     */
    async getTextDocument(identity) {
        await this.ensureInitialized();
        return this.provider.getDocument(identity);
    }
    /**
     * 获取文档段落
     */
    async getTextDocumentParagraphs(identity) {
        await this.ensureInitialized();
        return this.provider.getParagraphs(identity);
    }
    /**
     * 获取所有文档
     */
    async getAllTextDocuments() {
        await this.ensureInitialized();
        try {
            return await this.provider.getAllDocuments();
        }
        catch (error) {
            this.logger.error('[DocumentStorageService] Failed to get all documents', { error });
            throw error;
        }
    }
    async getReadingPosition(identity) {
        const doc = await this.provider.getDocument(identity);
        return doc?.readingAnchor;
    }
    async updateReadingPosition(identity, position) {
        await this.provider.updateDocument(identity, {
            readingAnchor: position,
            lastAccessTime: Date.now(),
        });
    }
    /**
     * 删除文档
     */
    async deleteTextDocument(identity) {
        await this.ensureInitialized();
        try {
            const translationService = TranslationStorageService.getInstance();
            await this.provider.transaction(async () => {
                await this.provider.deleteDocument(identity);
                await this.provider.deleteParagraphs(identity);
                await translationService.deleteTranslationsForTextDocument(identity);
            });
        }
        catch (error) {
            this.logger.error(`[DocumentStorageService] Failed to delete document (id: ${identity.id}, type: ${identity.type})`, { error, identity });
            throw error;
        }
    }
    /**
     * 获取统计信息
     */
    async getStats() {
        await this.ensureInitialized();
        try {
            const count = await this.provider.getDocumentCount();
            return { documentCount: count };
        }
        catch (error) {
            this.logger.error('[DocumentStorageService] Failed to get document statistics', { error });
            throw error;
        }
    }
    /**
     * 查找重复文档
     * 基于文本长度、语言和内容预览进行比较
     */
    async findDuplicateDocument(text, language, targetLanguage) {
        await this.ensureInitialized();
        // 检查文档数量
        const docCount = await this.provider.getDocumentCount();
        if (docCount === 0) {
            return null;
        }
        // 计算特征
        const textLength = text.length;
        const textPreview = text.substring(0, 100) + (text.length > 100 ? '...' : '');
        // 获取所有文档
        const allDocs = await this.provider.getAllDocuments();
        // 查找匹配的文档
        const duplicate = allDocs.find((doc) => doc.size === textLength &&
            doc.language === language &&
            doc.targetLanguage === targetLanguage &&
            doc.preview === textPreview);
        return duplicate?.id ?? null;
    }
    /**
     * 生成文档标题
     * 从文本开头提取有意义的标题
     */
    generateTitle(text) {
        if (!text)
            return 'Untitled Document';
        const firstLine = text.split('\n')[0].trim();
        const TITLE_MAX_LENGTH = 60;
        if (firstLine.length > TITLE_MAX_LENGTH) {
            return firstLine.substring(0, TITLE_MAX_LENGTH) + '...';
        }
        if (firstLine.length > 0) {
            return firstLine;
        }
        // 回退处理
        const firstWords = text.trim().substring(0, TITLE_MAX_LENGTH);
        return firstWords + (text.length > TITLE_MAX_LENGTH ? '...' : '') || 'Untitled Document';
    }
    /**
     * 检查文档字段（调试用）
     */
    async checkDocumentFields() {
        await this.ensureInitialized();
    }
}
// 单例实例
DocumentStorageService.instance = null;
//# sourceMappingURL=DocumentStorageService.js.map